/*
    © 2023 EcomGraduates.com
    https://www.ecomgraduates.com
*/

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector("#predictive-search");
    this.alert = this.querySelector("#predictive-search-alert");
    this.footer = this.closest("#offcanvas-search").querySelector(".offcanvas-footer");
    this.popularProducts = this.closest("#offcanvas-search").querySelector("#search-popular-products-wrapper");
    this.speechBtn = this.querySelector(".btn-search-by-voice");

    this.input.addEventListener(
      "input",
      this.debounce((event) => {
        this.onChange();
      }, 300).bind(this)
    );

    document.querySelector("#offcanvas-search")?.addEventListener("shown.bs.offcanvas", () => {
      this.input.focus();
    });

    if (window.SpeechRecognition || window.webkitSpeechRecognition) {
      this.speechBtn?.addEventListener("click", () => {
        this.speechRecognition();
      });
    } else {
      this.speechBtn.remove();
    }

    document.querySelectorAll("#offcanvas-search .btn-atc").forEach((btn) => {
      btn.addEventListener("click", () => {
        setTimeout(() => {
          bootstrap.Offcanvas.getOrCreateInstance("#offcanvas-search").hide();
        }, 300);
      });
    });
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    this.footer.querySelector('[name="q"]').value = searchTerm;
    this.footer.querySelector(".btn").textContent = `${this.footer.querySelector(".btn").dataset.textSearchFor} "${searchTerm}"`;

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  async getSearchResults(searchTerm) {
    // Wait for Constructor.io client to be available from constructor-search.js
    if (!window.constructorioClient || !window.constructorioClient.autocomplete) {
      return this.getShopifySearchResults(searchTerm);
    }

    try {
      // Use the initialized Constructor.io client from constructor-search.js
      const constructorClient = window.constructorioClient;

      // Official API call following exact documentation
      const response = await constructorClient.autocomplete.getAutocompleteResults(searchTerm, {
        resultsPerSection: {
          Products: 8,
          "Search Suggestions": 5,
        },
        numResults: 8,
        filters: {},
      });

      this.renderConstructorResults(response, searchTerm);
      this.open();

      // Official tracking for autocomplete - trackInputFocus
      if (constructorClient.tracker) {
        constructorClient.tracker.trackInputFocus({
          term: searchTerm,
        });
      }
    } catch (error) {
      console.warn("Constructor.io autocomplete failed, falling back to Shopify:", error);
      return this.getShopifySearchResults(searchTerm);
    }
  }

  renderConstructorResults(response, searchTerm) {
    if (!response.sections) {
      return;
    }

    this.results.innerHTML = "";

    if (response.sections["Search Suggestions"]) {
      const suggestionsHtml = response.sections["Search Suggestions"]
        .map((suggestion, index) => {
          const suggestionValue = suggestion.value || suggestion.data?.value || suggestion;
          return `
            <li>
              <a href="/search?q=${encodeURIComponent(suggestionValue)}" 
                 class="link-body-emphasis text-decoration-none autocomplete-suggestion"
                 data-suggestion="${suggestionValue}"
                 data-index="${index}"
                 data-cnstrc-item-section="Search Suggestions"
                 data-cnstrc-item-id="${suggestionValue}"
                 data-cnstrc-item-name="${suggestionValue}">
                ${suggestionValue}
              </a>
            </li>
          `;
        })
        .join("");

      if (suggestionsHtml) {
        this.results.innerHTML += `
          <div class="mt-4 mb-3">
            <h6 class="h6 fw-bold mb-2 border-bottom pb-2">Suggestions</h6>
            <ul class="list-unstyled">${suggestionsHtml}</ul>
          </div>
        `;
      }
    }

    if (response.sections.Products && response.sections.Products.length > 0) {
      const productsHtml = response.sections.Products.map((product, index) => {
        const data = product.data || product;

        const title =
          product.value || data.title || data.name || data.product_name || data.display_name || data.item_name || "Untitled";
        const image = data.image_url || data.thumbnail_url || data.image || data.product_image || data.featured_image || "";
        const url = data.url || data.product_url || data.link || `/products/${data.handle || data.slug || ""}`;

        const price = data.price || data.facets?.price || data.cost || "";
        const comparePrice =
          data.compareAtPrice || data.compare_at_price || data.original_price || data.list_price || data.regular_price || "";

        const itemId = data.id || data.product_id || data.item_id || data.handle || "";
        const available = data.available !== false;

        // Format prices correctly
        const formattedPrice = price ? `$${parseFloat(price).toFixed(2)}` : "";
        const formattedComparePrice = comparePrice ? `$${parseFloat(comparePrice).toFixed(2)}` : "";

        return `
          <li class="product-item py-3" 
              data-cnstrc-item-id="${itemId}" 
              data-cnstrc-item-name="${title}" 
              data-cnstrc-item-section="Products">
            <div class="row align-items-center mx-n3">
              <div class="col-4 px-3">
                <a href="${url}">
                  <img 
                    class="product-item-img img-fluid me-3 rounded"
                    src="${image}"
                    alt="${title}"
                    width="600"
                    height="600"
                    loading="lazy"
                    style="aspect-ratio: 1; object-fit: cover;">
                </a>
              </div>
              <div class="col-8 px-3 text-start">
                <h4 class="product-item-title h6 mb-1 text-start">
                  <a href="${url}" class="link-dark product-link">
                    ${title}
                  </a>
                </h4>
                <p class="product-item-price small mb-3 text-start">
                  ${
                    formattedComparePrice && formattedComparePrice !== formattedPrice
                      ? `<span class="product-item-price-compare text-dark me-1">
                       <s>${formattedComparePrice}</s>
                     </span>`
                      : ""
                  }
                  <span class="product-item-price-final text-danger">
                    ${formattedPrice}
                  </span>
                </p>
                <div class="form-wrapper text-start">
                  <div class="d-flex justify-content-start">
                    <button
                      class="btn-atc btn btn-sm btn-primary px-4 flex-shrink-0" 
                      type="button"
                      data-product-url="${url}"
                      ${!available ? "disabled" : ""}>
                      ${available ? "ADD" : "Sold Out"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </li>
        `;
      }).join("");

      this.results.innerHTML += `
        <div class="mt-4">
          <h6 class="h6 fw-bold mb-2 border-bottom pb-2">Products</h6>
          <ul class="product-list list-unstyled mb-0">${productsHtml}</ul>
        </div>
      `;

      this.results.querySelectorAll(".btn-atc").forEach((button) => {
        button.addEventListener("click", (e) => {
          const productUrl = e.target.dataset.productUrl;
          if (productUrl) {
            window.location.href = productUrl;
          }
        });
      });
    }

    this.addAutocompleteTrackingListeners(searchTerm);
  }

  addConstructorTrackingToExistingProducts() {
    const popularProductsWrapper = document.getElementById("search-popular-products-wrapper");
    if (popularProductsWrapper) {
      const existingProducts = popularProductsWrapper.querySelectorAll(".product-item");

      existingProducts.forEach((item, index) => {
        const productLink = item.querySelector('a[href*="/products/"]');
        if (productLink && !item.hasAttribute("data-cnstrc-tracking-added")) {
          const productHandle = productLink.href.split("/products/")[1]?.split("?")[0];

          if (productHandle) {
            item.setAttribute("data-cnstrc-item-id", productHandle);
            item.setAttribute("data-cnstrc-tracking-added", "true");

            productLink.addEventListener("click", function (e) {
              if (window.constructorioClient?.tracker) {
                window.constructorioClient.tracker.trackAutocompleteSelect({
                  term: productHandle,
                  section: "Products",
                });
              }
            });
          }
        }
      });
    }
  }

  addAutocompleteTrackingListeners(searchTerm) {
    const suggestionLinks = this.results.querySelectorAll(".autocomplete-suggestion");
    suggestionLinks.forEach((link, index) => {
      link.addEventListener("click", (e) => {
        const suggestion = e.target.dataset.suggestion;
        if (window.constructorioClient?.tracker) {
          window.constructorioClient.tracker.trackAutocompleteSelect({
            term: suggestion,
            original_query: searchTerm,
            section: "Search Suggestions",
          });
        }
      });
    });

    const productLinks = this.results.querySelectorAll(".product-item .product-link");
    productLinks.forEach((link, index) => {
      link.addEventListener("click", (e) => {
        const productItem = e.target.closest(".product-item");
        const itemId = productItem?.dataset.cnstrcItemId;
        const itemName = e.target.textContent.trim();

        if (itemId && itemName && window.constructorioClient?.tracker) {
          window.constructorioClient.tracker.trackAutocompleteSelect({
            term: itemName,
            original_query: searchTerm,
            section: "Products",
            item_id: itemId,
          });
        }
      });
    });
  }

  async getShopifySearchResults(searchTerm) {
    let resourcesType = "product";

    if (this.input.dataset.searchCollections === "true") {
      resourcesType = `${resourcesType},collection`;
    }
    if (this.input.dataset.searchPages === "true") {
      resourcesType = `${resourcesType},page`;
    }
    if (this.input.dataset.searchArticles === "true") {
      resourcesType = `${resourcesType},article`;
    }

    const response = await fetch(
      `/search/suggest?q=${searchTerm}&resources[type]=${resourcesType}&resources[limit]=10&section_id=predictive-search`
    );

    if (!response.ok) {
      const error = new Error(response.status);
      this.close();
      throw error;
    }

    const text = await response.text();
    const resultsMarkup = new DOMParser()
      .parseFromString(text, "text/html")
      .querySelector("#shopify-section-predictive-search").innerHTML;
    this.results.innerHTML = resultsMarkup;

    this.addConstructorTrackingToExistingProducts();

    this.open();
  }

  open() {
    if (!this.results) {
      return;
    }

    this.results.style.display = "block";

    const countResults = this.results.querySelectorAll(".product-item").length;
    const hasSuggestions = this.results.querySelector(".autocomplete-suggestion");

    switch (countResults) {
      case 0:
        this.alert.textContent = this.alert.dataset.textNoResults;
        break;
      case 1:
        this.alert.textContent = this.alert.dataset.textResultFound;
        break;
      default:
        this.alert.textContent = this.alert.dataset.textResultsFound.replace("[count]", countResults);
        break;
    }

    this.footer.removeAttribute("hidden");

    window.SPR?.initDomEls();
    window.SPR?.loadBadges();

    if (countResults === 0 && hasSuggestions) {
      this.popularProducts?.removeAttribute("hidden");
    } else {
      this.popularProducts?.setAttribute("hidden", "hidden");
    }
  }

  close() {
    this.results.style.display = "none";
    this.alert.textContent = "";
    this.footer.setAttribute("hidden", "hidden");

    this.popularProducts?.removeAttribute("hidden");
  }

  speechRecognition() {
    // eslint-disable-next-line no-undef, no-use-before-define, no-var
    let SpeechRecognition = SpeechRecognition || webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.onstart = () => {
      this.speechBtn.classList.add("speech-started");

      setTimeout(() => {
        this.speechBtn.classList.remove("speech-started");
      }, 5000);
    };

    recognition.onspeechend = (event) => {
      this.speechBtn.classList.remove("speech-started");
    };

    recognition.onresult = (event) => {
      if (event.results) {
        this.input.value = event.results[0][0].transcript;
        this.onChange();
      }
    };

    recognition.start();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define("predictive-search", PredictiveSearch);

/**
 * SearchPageAutocomplete - Handles autocomplete for the main search page
 */
class SearchPageAutocomplete {
  constructor() {
    this.input = document.getElementById("main-search-input");
    this.results = document.getElementById("main-search-autocomplete");
    this.form = document.getElementById("main-search-form");

    if (!this.input || !this.results) {
      console.log("Search page autocomplete elements not found");
      return;
    }

    this.isVisible = false;
    this.debounceTimer = null;

    this.bindEvents();
    console.log("🔍 Search page autocomplete initialized");
  }

  bindEvents() {
    // Input events
    this.input.addEventListener("input", (e) => this.onInput(e));
    this.input.addEventListener("focus", () => this.onFocus());
    this.input.addEventListener("blur", (e) => this.onBlur(e));

    // Form submission
    this.form.addEventListener("submit", (e) => this.onSubmit(e));

    // Close on escape
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isVisible) {
        this.hide();
        this.input.blur();
      }
    });

    // Close when clicking outside
    document.addEventListener("click", (e) => {
      if (!this.input.contains(e.target) && !this.results.contains(e.target)) {
        this.hide();
      }
    });
  }

  onInput(e) {
    const query = e.target.value.trim();

    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      if (query.length >= 2) {
        this.getAutocompleteResults(query);
      } else {
        this.hide();
      }
    }, 300);
  }

  onFocus() {
    const query = this.input.value.trim();
    if (query.length >= 2) {
      this.getAutocompleteResults(query);
    }
  }

  onBlur(e) {
    // Delay hiding to allow clicks on results
    setTimeout(() => {
      if (!this.results.contains(document.activeElement)) {
        this.hide();
      }
    }, 150);
  }

  onSubmit(e) {
    const query = this.input.value.trim();
    if (query && window.constructorioClient?.tracker) {
      window.constructorioClient.tracker.trackSearchSubmit({
        term: query,
        section: "Products",
      });
    }
    this.hide();
  }

  async getAutocompleteResults(searchTerm) {
    try {
      console.log(`🔍 [SearchPageAutocomplete] Getting results for: "${searchTerm}"`);
      console.log("Constructor.io client available?", !!window.constructorioClient);
      console.log("Autocomplete method available?", !!window.constructorioClient?.autocomplete);

      if (!window.constructorioClient?.autocomplete) {
        console.warn("Constructor.io autocomplete not available");
        return;
      }

      console.log(`🔍 Getting autocomplete results for: "${searchTerm}"`);

      // Track input focus
      if (window.constructorioClient.tracker) {
        window.constructorioClient.tracker.trackInputFocus({
          term: searchTerm,
          section: "Products",
        });
      }

      const response = await window.constructorioClient.autocomplete.getAutocompleteResults(searchTerm, {
        resultsPerSection: {
          Products: 6,
          "Search Suggestions": 5,
        },
        section: "Products",
      });

      this.renderResults(response, searchTerm);
      this.show();
    } catch (error) {
      console.error("Autocomplete error:", error);
      this.hide();
    }
  }

  renderResults(response, searchTerm) {
    console.log("🎨 [SearchPageAutocomplete] Rendering results:", response);

    if (!response.sections) {
      console.log("❌ No sections in response");
      this.hide();
      return;
    }

    let html = "";

    // Search suggestions
    if (response.sections["Search Suggestions"]?.length > 0) {
      html += `
        <div class="autocomplete-section">
          <h6>Suggestions</h6>
          <ul>
            ${response.sections["Search Suggestions"]
              .map(
                (suggestion, index) => `
              <li>
                <a href="/search?q=${encodeURIComponent(suggestion.value)}" 
                   class="autocomplete-suggestion"
                   data-suggestion="${suggestion.value}"
                   data-index="${index}"
                   data-cnstrc-item-section="Search Suggestions"
                   data-cnstrc-item-id="${suggestion.value}"
                   data-cnstrc-item-name="${suggestion.value}">
                  ${suggestion.value}
                </a>
              </li>
            `
              )
              .join("")}
          </ul>
        </div>
      `;
    }

    // Products
    if (response.sections.Products?.length > 0) {
      html += `
        <div class="autocomplete-section">
          <h6>Products</h6>
          <ul>
            ${response.sections.Products.map((product, index) => {
              const data = product.data || product;
              const title = data.display_name || data.title || data.name || "";
              const url = data.url || `/products/${data.handle || data.id || ""}`;
              const image = data.image_url || data.image || "";
              const price = data.price || data.display_price || "";
              const itemId = data.id || data.product_id || data.item_id || data.handle || "";

              const formattedPrice = price ? `$${parseFloat(price).toFixed(2)}` : "";

              return `
                <li>
                  <a href="${url}" 
                     class="autocomplete-product"
                     data-cnstrc-item-id="${itemId}" 
                     data-cnstrc-item-name="${title}" 
                     data-cnstrc-item-section="Products">
                    ${image ? `<img src="${image}" alt="${title}">` : `<div class="placeholder-image"></div>`}
                    <div class="product-info">
                      <span class="product-name">${title}</span>
                      ${formattedPrice ? `<span class="product-price">${formattedPrice}</span>` : ""}
                    </div>
                  </a>
                </li>
              `;
            }).join("")}
          </ul>
        </div>
      `;
    }

    if (html) {
      this.results.innerHTML = html;
      this.addClickTracking(searchTerm);
    } else {
      this.hide();
    }
  }

  addClickTracking(searchTerm) {
    // Track suggestion clicks
    this.results.querySelectorAll(".autocomplete-suggestion").forEach((link, index) => {
      link.addEventListener("click", () => {
        if (window.constructorioClient?.tracker) {
          window.constructorioClient.tracker.trackAutocompleteSelect({
            term: link.dataset.suggestion,
            section: "Search Suggestions",
            originalQuery: searchTerm,
          });
        }
      });
    });

    // Track product clicks
    this.results.querySelectorAll(".autocomplete-product").forEach((link, index) => {
      link.addEventListener("click", () => {
        if (window.constructorioClient?.tracker) {
          window.constructorioClient.tracker.trackAutocompleteSelect({
            term: link.dataset.cnstrcItemId,
            section: "Products",
            originalQuery: searchTerm,
          });
        }
      });
    });
  }

  show() {
    this.results.style.display = "block";
    this.results.classList.remove("d-none");
    this.isVisible = true;
    console.log("🔍 [SearchPageAutocomplete] Showing results");
  }

  hide() {
    this.results.style.display = "none";
    this.results.classList.add("d-none");
    this.isVisible = false;
    console.log("🔍 [SearchPageAutocomplete] Hiding results");
  }
}

window.closeSearchOffcanvas = (btn, event) => {
  setTimeout(() => {
    bootstrap.Offcanvas.getOrCreateInstance("#offcanvas-search").hide();
  }, 300);
};

// Initialize search page autocomplete when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  if (document.getElementById("main-search-input")) {
    // Wait for Constructor.io to be ready
    const initSearchPageAutocomplete = () => {
      if (window.constructorioClient) {
        console.log("🔍 Initializing SearchPageAutocomplete with Constructor.io");
        new SearchPageAutocomplete();
      } else {
        console.log("⏳ Waiting for Constructor.io client to initialize...");
        setTimeout(initSearchPageAutocomplete, 100);
      }
    };
    initSearchPageAutocomplete();
  }
});
